import { Box, Paper, Skeleton, Text, Title } from '@mantine/core'
import { LineChart } from '@mantine/charts'

import { useWhatsApiPeriod } from '@/context/WhatsApiPeriodContext'
import { useThroughput, isNoApiKeys } from '@/hooks/useWhatsApiStats'
import type { EscopoWhatsApi } from '@/hooks/useEscopoWhatsApi'

const CHART_HEIGHT = 280

function formatTimestamp(value: string): string {
  try {
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return value
    return d.toLocaleString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit',
    })
  } catch {
    return value
  }
}

interface LatencyChartProps {
  escopo?: EscopoWhatsApi
}

export function LatencyChart({ escopo }: LatencyChartProps = {}) {
  const { period, bucket } = useWhatsApiPeriod()
  const query = useThroughput(period, bucket, { escopo })

  return (
    <Paper shadow="xs" p="md" withBorder>
      <Title order={5} mb="sm">
        Latência média
      </Title>

      {query.isLoading && <Skeleton height={CHART_HEIGHT} radius="md" />}

      {!query.isLoading && (!query.data || isNoApiKeys(query.data) || !query.data.points?.length) && (
        <Box
          style={{
            height: CHART_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          data-testid="latency-empty"
        >
          <Text c="dimmed" size="sm">
            Nenhum dado disponível para este período
          </Text>
        </Box>
      )}

      {!query.isLoading && query.data && !isNoApiKeys(query.data) && (query.data.points?.length ?? 0) > 0 && (
        <LineChart
          h={CHART_HEIGHT}
          data={(query.data.points ?? []).map((p) => ({
            ...p,
            timestamp: formatTimestamp(p.timestamp),
          }))}
          dataKey="timestamp"
          series={[{ name: 'avg_latency_ms', label: 'Latência (ms)', color: 'blue.6' }]}
          curveType="linear"
          withXAxis
          withYAxis
          withTooltip
          unit=" ms"
        />
      )}
    </Paper>
  )
}
